import apiClient from './api';

export type TenantMetadataValue =
  | string
  | number
  | boolean
  | null
  | TenantMetadataValue[]
  | { [key: string]: TenantMetadataValue };

/** Tenant metadata - custom fields stored as JSON values */
export type TenantMetadata = Record<string, TenantMetadataValue>;

export interface Tenant {
  id: string;
  name: string;
  slug: string;
  idp_realm_id?: string;
  realm_name?: string;
  custom_domain?: string;
  default_language?: string;
  allowed_languages?: string[];
  metadata?: TenantMetadata;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
  agent_count?: number;
  api_key_count?: number;
  document_count?: number;
  last_user_login?: string | null;
  // Extended fields
  description?: string;
  logo_url?: string;
  primary_color?: string;
  secondary_color?: string;
  max_users?: number;
  max_agents?: number;
  max_llm_requests_per_day?: number;
  status?: string;
  is_active?: boolean;
  onboarding_enabled?: boolean;
  onboarding_config?: OnboardingConfig;
  settings?: TenantSettings;
}

export interface CreateTenantInput {
  name: string;
  slug: string;
  idp_realm_id?: string;
  max_users?: number;
  max_agents?: number;
  max_llm_requests_per_day?: number;
}

export interface UpdateTenantInput {
  name?: string;
  idp_realm_id?: string;
  metadata?: TenantMetadata;
}

export interface TenantSettings {
  welcome_title?: string;
  welcome_subtitle?: string;
  show_quick_actions?: boolean;
  show_modules?: boolean;
  show_stats?: boolean;
  custom_css?: string;
  // "v1" = legacy layout, "v2" = redesigned landing/chat/workspace/cockpit/workflow.
  // Default for existing tenants stays "v1" so nobody is surprised.
  layout_version?: 'v1' | 'v2';
  regulation_autopilot?: boolean;
  policy_autopilot?: boolean;
  onboarding_enabled?: boolean;
  onboarding_config?: Record<string, unknown>;
  partner_onboarding_enabled?: boolean;
  partner_onboarding_config?: Record<string, unknown>;
  feature_overrides?: Record<string, unknown>;
}

export interface UpdateTenantFullInput {
  name?: string;
  description?: string;
  logo_url?: string;
  primary_color?: string;
  secondary_color?: string;
  max_users?: number;
  max_agents?: number;
  max_llm_requests_per_day?: number;
  settings?: TenantSettings;
  metadata?: TenantMetadata;
}

export interface OnboardingConfig {
  skip_steps?: string[];
  custom_agents?: Record<string, string>;
  required_regulations?: string[];
  custom_welcome_message?: string;
  show_compliance_profile?: boolean;
  auto_start?: boolean;
}

export interface OnboardingConfigInput {
  enabled: boolean;
  config: OnboardingConfig;
}

export interface PartnerOnboardingConfig {
  skip_steps?: string[];
  custom_welcome_message?: string;
  default_integration_type?: string;
  recommended_sdks?: string[];
  require_billing_setup?: boolean;
  require_company_verification?: boolean;
  auto_start?: boolean;
  show_sandbox_environment?: boolean;
}

export interface PartnerOnboardingConfigInput {
  enabled: boolean;
  config: PartnerOnboardingConfig;
}

export type BillingReadiness =
  | 'ready'
  | 'configured'
  | 'needs_setup'
  | 'needs_customer'
  | 'needs_subscription'
  | 'scheduled_cancel'
  | 'attention'
  | 'not_required';

export interface OnboardingAttentionItem {
  type: string;
  severity: 'info' | 'warning' | 'error';
  count?: number;
  message: string;
}

export interface BasicOnboardingSession {
  user_id: string;
  user_name?: string | null;
  email?: string | null;
  status: 'completed' | 'skipped' | 'in_progress';
  completed_steps: number;
  total_steps: number;
  selected_agent_id?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  skipped_at?: string | null;
  updated_at?: string | null;
}

export interface ComplianceOnboardingSession {
  user_id: string;
  user_name?: string | null;
  email?: string | null;
  current_step: number;
  total_steps: number;
  status: 'active' | 'paused' | 'completed' | 'skipped';
  started_at?: string | null;
  completed_at?: string | null;
  paused_at?: string | null;
  updated_at?: string | null;
}

export interface InvitationActivity {
  id: string;
  email: string;
  role: string;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  expires_at?: string | null;
  accepted_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  invited_by?: string | null;
}

export interface BillingInvoiceSnapshot {
  status: string;
  amount_cents: number;
  currency: string;
  created_at?: string | null;
  paid_at?: string | null;
  hosted_invoice_url?: string | null;
}

export interface TenantOnboardingOps {
  tenant: {
    id: string;
    name: string;
    slug: string;
    created_at: string;
    updated_at: string;
    onboarding_enabled: boolean;
    partner_onboarding: {
      enabled: boolean;
      auto_start: boolean;
      require_billing_setup: boolean;
      show_sandbox_environment: boolean;
      recommended_sdks: string[];
      default_integration_type?: string | null;
    };
  };
  tables: {
    user_onboarding: boolean;
    compliance_onboarding: boolean;
    invitations: boolean;
    billing: boolean;
    payment_history: boolean;
  };
  attention_items: OnboardingAttentionItem[];
  user_onboarding: {
    total_users: number;
    total_sessions: number;
    completed_sessions: number;
    skipped_sessions: number;
    in_progress_sessions: number;
    not_started_users: number;
    average_completed_steps: number;
    last_activity_at?: string | null;
    recent_sessions: BasicOnboardingSession[];
  };
  compliance_onboarding: {
    enabled: boolean;
    total_users: number;
    total_sessions: number;
    active_sessions: number;
    paused_sessions: number;
    completed_sessions: number;
    skipped_sessions: number;
    not_started_users: number;
    average_current_step: number;
    last_activity_at?: string | null;
    recent_sessions: ComplianceOnboardingSession[];
  };
  invitations: {
    total: number;
    pending: number;
    accepted: number;
    expired: number;
    revoked: number;
    last_activity_at?: string | null;
    recent: InvitationActivity[];
  };
  billing: {
    require_billing_setup: boolean;
    partner_onboarding_enabled: boolean;
    readiness: BillingReadiness;
    subscription_status?: string | null;
    plan_name?: string | null;
    plan_slug?: string | null;
    has_stripe_customer: boolean;
    has_stripe_subscription: boolean;
    cancel_at_period_end: boolean;
    current_period_end?: string | null;
    trial_end?: string | null;
    invoice_count: number;
    paid_invoice_count: number;
    open_invoice_count: number;
    last_invoice?: BillingInvoiceSnapshot | null;
  };
  generated_at: string;
}

/**
 * Roles the onboarding wizard hands to the first people of a new tenant: the
 * api-gateway's TENANT_ASSIGNABLE_ROLES (routes/admin.py), least rights first.
 * `admin` is still accepted for data entered before; the wizard offers
 * tenant_admin for it.
 */
export const INITIAL_TENANT_USER_ROLES = ['viewer', 'auditor', 'management', 'user', 'compliance_officer', 'tenant_admin'] as const;
export type InitialTenantUserRole = (typeof INITIAL_TENANT_USER_ROLES)[number] | 'admin';

export interface InitialTenantUserInput {
  id?: string;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: InitialTenantUserRole;
}

export interface InviteUsersResult {
  succeeded: number;
  failed: number;
  results: Array<{
    email: string;
    success: boolean;
    error?: string;
  }>;
}

function normalizeExternalTenantUrl(domainOrUrl: string): string {
  if (/^https?:\/\//i.test(domainOrUrl)) {
    return domainOrUrl;
  }

  return `https://${domainOrUrl}`;
}

export function getTenantLandingUrl(tenant: Pick<Tenant, 'slug' | 'custom_domain'>): string {
  if (tenant.custom_domain && tenant.custom_domain.trim()) {
    return normalizeExternalTenantUrl(tenant.custom_domain.trim());
  }

  // Derive from the deployment's frontend host so non-agentic360.de boxes
  // (e.g. the softScheck box → agent-test.softscheck-apac.com) link to their
  // own tenant landing instead of the hardcoded agentic360.de.
  const base = (import.meta.env.VITE_APP_URL || 'https://agentic360.de').replace(/\/$/, '');
  return `${base}/${tenant.slug}`;
}

/** Module configuration values */
export type ModuleConfigValue = string | number | boolean | null;

export interface ModulesConfigInput {
  enabled_modules: string[];
  config?: Record<string, ModuleConfigValue>;
}

type Localized = Partial<Record<'de' | 'en' | 'es', string>>;

export type FrameworkDecision = 'applies' | 'likely' | 'needs_facts' | 'voluntary' | 'not_relevant';

export interface TenantFrameworkDecision {
  framework_key: string;
  name: string;
  category: string | null;
  decision: FrameworkDecision;
  decision_label: Localized | null;
  reason: Localized | null;
  missing_facts: { fact: string; label: Localized; source: string }[] | null;
  legal_dates: { date: string; key: string; label: Localized }[] | null;
  contractual: boolean | null;
  source: string | null;
}

export interface TenantAssignedAct {
  regulation_id: string;
  title: string;
  assigned_at: string | null;
  assigned_by_name: string | null;
}

export interface TenantFrameworks {
  tenant_id: string;
  engine: {
    ruleset_version: string | null;
    ruleset_review: { decision: string; reviewer_name: string; reviewed_at: string } | null;
    has_facts: boolean;
    facts_version: number;
    decisions: TenantFrameworkDecision[];
  } | null;
  assigned_acts: TenantAssignedAct[];
  bulk_assigned: number;
}

export interface TenantStats {
  tenant_id: string;
  statistics: {
    agents: {
      total: number;
      created_last_30_days: number;
    };
    api_keys: {
      total: number;
      active: number;
    };
    documents: {
      total: number;
    };
    knowledge_collections: {
      total: number;
    };
  };
  generated_at: string;
}

export interface TenantMailAccountConfig {
  tenant_id: string;
  configured: boolean;
  email_address: string;
  from_name: string;
  reply_to: string;
  smtp_host: string;
  smtp_port: number;
  smtp_username: string;
  smtp_password: string;
  has_password: boolean;
  inbound_enabled: boolean;
  imap_host: string;
  imap_port: number;
  imap_username: string;
  imap_password: string;
  has_imap_password: boolean;
  imap_mailbox: string;
  use_imaps: boolean;
  use_imap_starttls: boolean;
  imap_verify_tls_certificates: boolean;
  imap_poll_interval_minutes: number;
  imap_unread_only: boolean;
  use_starttls: boolean;
  use_smtps: boolean;
  verify_tls_certificates: boolean;
  is_default: boolean;
  is_active: boolean;
  metadata: Record<string, unknown>;
  updated_at?: string | null;
}

export interface TenantMailAccountInput {
  email_address: string;
  from_name?: string;
  reply_to?: string;
  smtp_host: string;
  smtp_port: number;
  smtp_username?: string;
  smtp_password?: string;
  inbound_enabled: boolean;
  imap_host?: string;
  imap_port?: number;
  imap_username?: string;
  imap_password?: string;
  imap_mailbox?: string;
  use_imaps: boolean;
  use_imap_starttls: boolean;
  imap_verify_tls_certificates: boolean;
  imap_poll_interval_minutes?: number;
  imap_unread_only: boolean;
  use_starttls: boolean;
  use_smtps: boolean;
  verify_tls_certificates: boolean;
  is_default: boolean;
  is_active: boolean;
  metadata?: Record<string, unknown>;
}

export interface TenantCalendarAccountConfig {
  tenant_id: string;
  configured: boolean;
  provider: 'caldav' | string;
  email_address: string;
  caldav_base_url: string;
  calendar_path: string;
  username: string;
  password: string;
  has_password: boolean;
  timezone: string;
  is_default: boolean;
  is_active: boolean;
  metadata: Record<string, unknown>;
  updated_at?: string | null;
}

export interface TenantCalendarAccountInput {
  provider: 'caldav' | string;
  email_address: string;
  caldav_base_url: string;
  calendar_path: string;
  username: string;
  password?: string;
  timezone: string;
  is_default: boolean;
  is_active: boolean;
  metadata?: Record<string, unknown>;
}

export interface TenantWhatsAppChannelConfig {
  tenant_id: string;
  configured: boolean;
  phone_number_id: string;
  business_account_id: string;
  display_name: string;
  webhook_verify_token: string;
  access_token: string;
  has_access_token: boolean;
  app_secret: string;
  has_app_secret: boolean;
  is_default: boolean;
  is_active: boolean;
  metadata: Record<string, unknown>;
  updated_at?: string | null;
}

export interface TenantWhatsAppChannelInput {
  phone_number_id: string;
  business_account_id?: string;
  display_name?: string;
  webhook_verify_token?: string;
  access_token?: string;
  app_secret?: string;
  is_default: boolean;
  is_active: boolean;
  metadata?: Record<string, unknown>;
}

export interface TenantCalendarRulesConfig {
  tenant_id: string;
  timezone: string;
  slot_duration_minutes: number;
  booking_window_days: number;
  buffer_before_minutes: number;
  buffer_after_minutes: number;
  min_notice_minutes: number;
  business_hours: Record<string, string[][]>;
  allowed_weekdays: number[];
  blackout_dates: string[];
  metadata: Record<string, unknown>;
}

export interface TenantCalendarRulesInput {
  timezone: string;
  slot_duration_minutes: number;
  booking_window_days: number;
  buffer_before_minutes: number;
  buffer_after_minutes: number;
  min_notice_minutes: number;
  business_hours: Record<string, string[][]>;
  allowed_weekdays: number[];
  blackout_dates: string[];
  metadata?: Record<string, unknown>;
}

const TENANTS_BASE = '/tenants';
const DEFAULT_ALLOWED_LANGUAGES = ['de', 'en', 'es'];

type TenantIdentityFields = {
  idp_realm_id?: unknown;
  realm_name?: unknown;
  keycloak_realm_name?: unknown;
  slug?: string;
};

function parseStringArray(value: unknown, fallback: string[] = DEFAULT_ALLOWED_LANGUAGES): string[] {
  if (Array.isArray(value)) {
    const items = value.map((item) => String(item).trim()).filter(Boolean);
    return items.length > 0 ? items : [...fallback];
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return [...fallback];
    }

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        const items = parsed.map((item) => String(item).trim()).filter(Boolean);
        return items.length > 0 ? items : [...fallback];
      }
    } catch {
      const items = trimmed
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
      return items.length > 0 ? items : [...fallback];
    }
  }

  return [...fallback];
}

function parseRecord<T>(value: unknown): T | undefined {
  if (!value) {
    return undefined;
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as T : undefined;
    } catch {
      return undefined;
    }
  }

  return typeof value === 'object' && !Array.isArray(value) ? value as T : undefined;
}

export function getTenantIdentityRealmName(tenant: TenantIdentityFields): string {
  const candidates = [
    tenant.idp_realm_id,
    tenant.realm_name,
    tenant.keycloak_realm_name,
    tenant.slug,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }

  return '';
}

function normalizeTenant(tenant: Tenant & { keycloak_realm_name?: string }): Tenant {
  const idpRealmId = getTenantIdentityRealmName(tenant) || undefined;

  return {
    ...tenant,
    idp_realm_id: idpRealmId,
    realm_name: idpRealmId,
    allowed_languages: parseStringArray(tenant.allowed_languages),
    metadata: parseRecord<TenantMetadata>(tenant.metadata) || tenant.metadata,
    settings: parseRecord<TenantSettings>(tenant.settings) || tenant.settings,
    onboarding_config: parseRecord<OnboardingConfig>(tenant.onboarding_config) || tenant.onboarding_config,
  };
}

function normalizeObjectRecord(value: unknown): Record<string, unknown> {
  return parseRecord<Record<string, unknown>>(value) || {};
}

function normalizeMailIntegration(payload: TenantMailAccountConfig): TenantMailAccountConfig {
  return {
    ...payload,
    inbound_enabled: Boolean(payload.inbound_enabled),
    imap_host: String(payload.imap_host || ''),
    imap_port: Number(payload.imap_port || 993),
    imap_username: String(payload.imap_username || ''),
    imap_password: String(payload.imap_password || ''),
    has_imap_password: Boolean(payload.has_imap_password),
    imap_mailbox: String(payload.imap_mailbox || 'INBOX'),
    use_imaps: payload.use_imaps !== false,
    use_imap_starttls: Boolean(payload.use_imap_starttls),
    imap_verify_tls_certificates: payload.imap_verify_tls_certificates !== false,
    imap_poll_interval_minutes: Number(payload.imap_poll_interval_minutes || 5),
    imap_unread_only: payload.imap_unread_only !== false,
    metadata: normalizeObjectRecord(payload.metadata),
  };
}

function normalizeCalendarIntegration(payload: TenantCalendarAccountConfig): TenantCalendarAccountConfig {
  return {
    ...payload,
    metadata: normalizeObjectRecord(payload.metadata),
  };
}

function normalizeWhatsAppIntegration(payload: TenantWhatsAppChannelConfig): TenantWhatsAppChannelConfig {
  return {
    ...payload,
    metadata: normalizeObjectRecord(payload.metadata),
  };
}

function normalizeCalendarRules(payload: TenantCalendarRulesConfig): TenantCalendarRulesConfig {
  return {
    ...payload,
    business_hours: normalizeObjectRecord(payload.business_hours) as Record<string, string[][]>,
    allowed_weekdays: Array.isArray(payload.allowed_weekdays)
      ? payload.allowed_weekdays.map((value) => Number(value)).filter((value) => Number.isInteger(value))
      : [],
    blackout_dates: Array.isArray(payload.blackout_dates)
      ? payload.blackout_dates.map((value) => String(value))
      : [],
    metadata: normalizeObjectRecord(payload.metadata),
  };
}

export const tenantsService = {
  /**
   * Get all tenants with pagination
   */
  async getAll(params?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    includeInactive?: boolean;
  }): Promise<{
    tenants: Tenant[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }> {
    const { page, limit, search, sortBy, sortOrder, includeInactive } = params ?? {};
    const requestParams: Record<string, number | string | boolean> = {};

    if (typeof limit === 'number') {
      requestParams.limit = limit;
      if (typeof page === 'number') {
        requestParams.skip = Math.max(page - 1, 0) * limit;
      }
    } else if (typeof page === 'number') {
      requestParams.skip = Math.max(page - 1, 0) * 20;
    }

    if (typeof search === 'string' && search.trim()) {
      requestParams.search = search.trim();
    }

    if (typeof sortBy === 'string' && sortBy) {
      requestParams.sort_by = sortBy;
      requestParams.sort_order = sortOrder === 'asc' ? 'asc' : 'desc';
    }

    if (includeInactive) {
      requestParams.include_inactive = true;
    }

    const response = await apiClient.get(TENANTS_BASE, { params: requestParams });
    return {
      ...response.data,
      tenants: Array.isArray(response.data?.tenants)
        ? response.data.tenants.map((tenant: Tenant) => normalizeTenant(tenant))
        : [],
    };
  },

  /**
   * Get tenant by ID
   */
  async getById(id: string): Promise<Tenant> {
    const response = await apiClient.get(`${TENANTS_BASE}/${id}`);
    return normalizeTenant(response.data);
  },

  /**
   * Create new tenant
   */
  async create(data: CreateTenantInput): Promise<Tenant> {
    const response = await apiClient.post(TENANTS_BASE, data);
    return normalizeTenant(response.data);
  },

  /**
   * Update tenant
   */
  async update(id: string, data: UpdateTenantInput): Promise<Tenant> {
    const response = await apiClient.patch(`${TENANTS_BASE}/${id}`, data);
    return normalizeTenant(response.data);
  },

  /**
   * Delete tenant
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`${TENANTS_BASE}/${id}`);
  },

  /**
   * Get tenant statistics
   */
  async getStats(id: string): Promise<TenantStats> {
    const response = await apiClient.get(`${TENANTS_BASE}/${id}/stats`);
    return response.data;
  },

  /**
   * Which frameworks concern the tenant per the compliance scanner's rules, and the legal acts
   * assigned to it; superadmins only
   */
  async getFrameworks(id: string): Promise<TenantFrameworks> {
    const response = await apiClient.get(`${TENANTS_BASE}/${encodeURIComponent(id)}/frameworks`);
    return response.data;
  },

  /**
   * Update tenant status
   */
  async updateStatus(id: string, is_active: boolean): Promise<Tenant> {
    const response = await apiClient.patch(`${TENANTS_BASE}/${id}/status`, {
      is_active,
    });
    return normalizeTenant(response.data);
  },

  /**
   * Update tenant language settings
   */
  async updateLanguage(
    id: string,
    default_language: string,
    allowed_languages?: string[]
  ): Promise<Tenant> {
    const response = await apiClient.patch(`${TENANTS_BASE}/${id}/language`, {
      default_language,
      allowed_languages,
    });
    return normalizeTenant(response.data);
  },

  /**
   * Update tenant with full data (for onboarding wizard)
   */
  async updateFull(id: string, data: UpdateTenantFullInput): Promise<Tenant> {
    const response = await apiClient.patch(`${TENANTS_BASE}/${id}`, data);
    return normalizeTenant(response.data);
  },

  /**
   * Get onboarding configuration for a tenant
   */
  async getOnboardingConfig(id: string): Promise<OnboardingConfigInput> {
    const response = await apiClient.get(`${TENANTS_BASE}/${id}/onboarding-config`);
    return response.data;
  },

  /**
   * Update onboarding configuration for a tenant
   */
  async updateOnboardingConfig(id: string, config: OnboardingConfigInput): Promise<void> {
    await apiClient.put(`${TENANTS_BASE}/${id}/onboarding-config`, config);
  },

  /**
   * Get partner onboarding configuration for a tenant
   */
  async getPartnerOnboardingConfig(id: string): Promise<PartnerOnboardingConfigInput> {
    const response = await apiClient.get(`${TENANTS_BASE}/${id}/partner-onboarding-config`);
    return response.data;
  },

  /**
   * Update partner onboarding configuration for a tenant
   */
  async updatePartnerOnboardingConfig(id: string, config: PartnerOnboardingConfigInput): Promise<void> {
    await apiClient.put(`${TENANTS_BASE}/${id}/partner-onboarding-config`, config);
  },

  /**
   * Get operational visibility into invitations, onboarding progress, and billing readiness.
   */
  async getOnboardingOps(id: string): Promise<TenantOnboardingOps> {
    const response = await apiClient.get(`${TENANTS_BASE}/${id}/onboarding-ops`);
    return response.data;
  },

  /**
   * Assign global agents to a tenant
   */
  async assignAgents(tenantId: string, agentIds: string[]): Promise<void> {
    await apiClient.post(`/governance/tenant-assignments/bulk`, {
      tenant_id: tenantId,
      agent_ids: agentIds,
    });
  },

  /**
   * Get assigned global agents for a tenant
   */
  async getAssignedAgents(tenantId: string): Promise<{
    assignments: Array<{
      id: string;
      global_agent_id: string;
      status: string;
      custom_config: Record<string, unknown>;
      assigned_at?: string | null;
      agent_name?: string;
      agent_description?: string;
      agent_type?: string;
    }>;
  }> {
    const response = await apiClient.get(`/governance/tenant-assignments/${tenantId}`);
    return response.data;
  },

  /**
   * Update tenant modules configuration
   */
  async updateModules(id: string, config: ModulesConfigInput): Promise<void> {
    await apiClient.put(`${TENANTS_BASE}/${id}/modules`, config);
  },

  /**
   * Get tenant modules
   */
  async getModules(id: string): Promise<{ modules: TenantModule[]; enabled_modules: string[] }> {
    const response = await apiClient.get(`${TENANTS_BASE}/${id}/modules`);
    return response.data;
  },

  /**
   * Create/invite initial users for a tenant.
   * Uses existing /users endpoint so users are created in IdP and local DB.
   */
  async inviteUsers(tenantId: string, users: InitialTenantUserInput[]): Promise<InviteUsersResult> {
    const results: InviteUsersResult['results'] = [];

    for (const user of users) {
      try {
        await apiClient.post('/users', {
          email: user.email,
          first_name: user.firstName || user.username || '',
          last_name: user.lastName || '',
          tenant_id: tenantId,
          role: user.role,
        });
        results.push({ email: user.email, success: true });
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        results.push({ email: user.email, success: false, error: message });
      }
    }

    const succeeded = results.filter((r) => r.success).length;
    const failed = results.length - succeeded;
    return { succeeded, failed, results };
  },

  async getMailIntegration(tenantId: string): Promise<TenantMailAccountConfig> {
    const response = await apiClient.get(`/admin/tenants/${tenantId}/integrations/mail`);
    return normalizeMailIntegration(response.data);
  },

  async updateMailIntegration(tenantId: string, data: TenantMailAccountInput): Promise<TenantMailAccountConfig> {
    const response = await apiClient.put(`/admin/tenants/${tenantId}/integrations/mail`, data);
    return normalizeMailIntegration(response.data);
  },

  async getCalendarIntegration(tenantId: string): Promise<TenantCalendarAccountConfig> {
    const response = await apiClient.get(`/admin/tenants/${tenantId}/integrations/calendar`);
    return normalizeCalendarIntegration(response.data);
  },

  async updateCalendarIntegration(
    tenantId: string,
    data: TenantCalendarAccountInput,
  ): Promise<TenantCalendarAccountConfig> {
    const response = await apiClient.put(`/admin/tenants/${tenantId}/integrations/calendar`, data);
    return normalizeCalendarIntegration(response.data);
  },

  async getWhatsAppIntegration(tenantId: string): Promise<TenantWhatsAppChannelConfig> {
    const response = await apiClient.get(`/admin/tenants/${tenantId}/integrations/whatsapp`);
    return normalizeWhatsAppIntegration(response.data);
  },

  async updateWhatsAppIntegration(
    tenantId: string,
    data: TenantWhatsAppChannelInput,
  ): Promise<TenantWhatsAppChannelConfig> {
    const response = await apiClient.put(`/admin/tenants/${tenantId}/integrations/whatsapp`, data);
    return normalizeWhatsAppIntegration(response.data);
  },

  async getCalendarRules(tenantId: string): Promise<TenantCalendarRulesConfig> {
    const response = await apiClient.get(`/admin/tenants/${tenantId}/calendar-rules`);
    return normalizeCalendarRules(response.data);
  },

  async updateCalendarRules(
    tenantId: string,
    data: TenantCalendarRulesInput,
  ): Promise<TenantCalendarRulesConfig> {
    const response = await apiClient.put(`/admin/tenants/${tenantId}/calendar-rules`, data);
    return normalizeCalendarRules(response.data);
  },

  // ---------------------------------------------------------------------------
  // Mail inbox (IMAP) — reads tenant's configured mail account
  // ---------------------------------------------------------------------------
  async fetchMailInbox(
    tenantId: string,
    options: {
      mailbox?: string;
      unread_only?: boolean;
      limit?: number;
      since_days?: number;
      include_body?: boolean;
    } = {},
  ): Promise<{
    tenant_id: string;
    account_email: string;
    mailbox: string;
    source: string;
    message_count: number;
    messages: Array<{
      uid: string;
      message_id?: string | null;
      subject: string;
      from_name?: string | null;
      from_email?: string | null;
      to?: string | null;
      date: string | null;
      flags: string[];
      is_unread: boolean;
      snippet: string;
      body?: string | null;
      mailbox: string;
    }>;
  }> {
    const response = await apiClient.post('/mail/inbox/messages', {
      tenant_id: tenantId,
      mailbox: options.mailbox,
      unread_only: options.unread_only ?? true,
      limit: options.limit ?? 20,
      since_days: options.since_days ?? 7,
      include_body: options.include_body ?? false,
    });
    return response.data;
  },

  // ---------------------------------------------------------------------------
  // Calendar scheduling (CalDAV) — find-slot + events
  // ---------------------------------------------------------------------------
  async findCalendarSlots(
    tenantId: string,
    params: {
      date_from: string;
      date_to: string;
      duration_minutes: number;
      limit?: number;
    },
  ): Promise<{
    tenant_id: string;
    provider: string;
    account_email: string;
    timezone: string;
    source: string;
    busy_events: Array<{ uid?: string; summary?: string; start: string; end: string }>;
    suggested_slots: Array<{ start: string; end: string; timezone?: string }>;
  }> {
    const response = await apiClient.post('/calendar/find-slot', {
      tenant_id: tenantId,
      ...params,
      limit: params.limit ?? 10,
    });
    return response.data;
  },

  async createCalendarEvent(
    tenantId: string,
    payload: {
      title: string;
      start: string;
      end: string;
      description?: string | null;
      location?: string | null;
      attendee_emails?: string[];
    },
  ): Promise<{
    tenant_id: string;
    event_id: string;
    title: string;
    start: string;
    end: string;
    status: string;
  }> {
    const response = await apiClient.post('/calendar/events', {
      tenant_id: tenantId,
      ...payload,
      attendee_emails: payload.attendee_emails ?? [],
    });
    return response.data;
  },
};

/** Module definition for tenant */
export interface TenantModule {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  enabled: boolean;
}
